// suggestionEngine.js — pure rummy analysis for the practice "Hint" / "Auto-Arrange" feature.
// No React, no side effects: takes a hand + wild joker and returns the best grouping,
// alternative combinations, "1 card away" hints, and the best discard.

const RANK_ORDER = {
  '2': 2, '3': 3, '4': 4, '5': 5, '6': 6, '7': 7, '8': 8, '9': 9,
  '10': 10, 'J': 11, 'Q': 12, 'K': 13, 'A': 14,
};

const POINT_VALUE = {
  '2': 2, '3': 3, '4': 4, '5': 5, '6': 6, '7': 7, '8': 8, '9': 9, '10': 10,
  'J': 10, 'Q': 10, 'K': 10, 'A': 10,
};

const orderOf = (rank) => RANK_ORDER[rank] ?? 0;

const suitSymbol = (suit) => ({ hearts: '♥', diamonds: '♦', clubs: '♣', spades: '♠' }[suit] || '');

export const isJokerCard = (card, wildJoker) => {
  if (!card) return false;
  if (card.isJoker || card.suit === 'joker' || card.rank === 'Joker') return true;
  if (wildJoker && card.rank === wildJoker.rank) return true;
  return false;
};

const cardPoints = (card, wildJoker) => (isJokerCard(card, wildJoker) ? 0 : (POINT_VALUE[card.rank] ?? 0));

// Greedy pure-sequence extraction for one suit's natural cards (longest consecutive runs).
function pureSequencesForSuit(suitCards) {
  const sorted = [...suitCards].sort((a, b) => orderOf(a.rank) - orderOf(b.rank));
  const used = new Set();
  const result = [];
  for (let i = 0; i < sorted.length; i++) {
    if (used.has(i)) continue;
    const run = [sorted[i]];
    used.add(i);
    let prev = orderOf(sorted[i].rank);
    for (let j = i + 1; j < sorted.length; j++) {
      if (used.has(j)) continue;
      if (orderOf(sorted[j].rank) === prev + 1) {
        run.push(sorted[j]);
        used.add(j);
        prev = orderOf(sorted[j].rank);
      }
    }
    if (run.length >= 3) result.push(run);
  }
  return result;
}

// "1 card away" hints + joker tip.
function buildHints(cards, wildJoker) {
  const hints = [];
  const naturals = cards.filter((c) => !isJokerCard(c, wildJoker));
  const jokers = cards.filter((c) => isJokerCard(c, wildJoker));
  const bySuit = {};
  naturals.forEach((c) => { (bySuit[c.suit] = bySuit[c.suit] || []).push(c); });
  Object.entries(bySuit).forEach(([suit, list]) => {
    const sorted = [...list].sort((a, b) => orderOf(a.rank) - orderOf(b.rank));
    for (let i = 0; i < sorted.length - 1; i++) {
      const gap = orderOf(sorted[i + 1].rank) - orderOf(sorted[i].rank);
      if (gap === 2) {
        const missing = Object.keys(RANK_ORDER).find((r) => RANK_ORDER[r] === orderOf(sorted[i].rank) + 1);
        hints.push(`${sorted[i].rank}${suitSymbol(suit)} + ${sorted[i + 1].rank}${suitSymbol(suit)} — add ${missing}${suitSymbol(suit)} for a pure sequence`);
      }
    }
  });
  if (jokers.length > 0) {
    hints.push(`You have ${jokers.length} joker(s) — use one to fill a missing card in a sequence`);
  }
  return hints.slice(0, 4);
}

// Main analysis entry point.
export function analyzeHand(cards, wildJoker = null) {
  const list = (cards || []).filter((c) => c && !c.isGroupSeparator && !c.isGroup);
  const naturals = list.filter((c) => !isJokerCard(c, wildJoker));

  // 1. pure sequences (greedy, per suit)
  const bySuit = {};
  naturals.forEach((c) => { (bySuit[c.suit] = bySuit[c.suit] || []).push(c); });
  const pureSeqs = [];
  Object.values(bySuit).forEach((suitCards) => {
    pureSequencesForSuit(suitCards).forEach((run) => pureSeqs.push({ cards: run, isPure: true, type: 'sequence' }));
  });

  const groupedIds = new Set();
  pureSeqs.forEach((g) => g.cards.forEach((c) => groupedIds.add(c.id)));

  // 2. sets from remaining naturals
  const remainingNaturals = naturals.filter((c) => !groupedIds.has(c.id));
  const byRank = {};
  remainingNaturals.forEach((c) => { (byRank[c.rank] = byRank[c.rank] || []).push(c); });
  const sets = [];
  Object.values(byRank).forEach((group) => {
    if (group.length >= 3) {
      sets.push({ cards: group, isPure: true, type: 'set' });
      group.forEach((c) => groupedIds.add(c.id));
    }
  });

  // 3. deadwood = everything not grouped
  const deadwood = list.filter((c) => !groupedIds.has(c.id));

  const hasPureSequence = pureSeqs.length > 0;
  const isValid = hasPureSequence && pureSeqs.length >= 2 && deadwood.length === 0;

  const bestDiscard = [...deadwood].sort((a, b) => cardPoints(b, wildJoker) - cardPoints(a, wildJoker))[0] || null;

  const groups = [...pureSeqs, ...sets];

  return {
    groups,
    deadwood,
    bestDiscard,
    hasPureSequence,
    isValid,
    hints: buildHints(list, wildJoker),
    deadwoodPoints: deadwood.reduce((sum, c) => sum + cardPoints(c, wildJoker), 0),
    combinations: groups.map((g) =>
      `${g.type === 'set' ? 'Set' : 'Pure sequence'} (${g.cards.map((c) => `${c.rank}${suitSymbol(c.suit)}`).join(' ')})`
    ),
  };
}

// Arrange helper: grouped cards first, then deadwood.
export function suggestArrangement(cards, wildJoker = null) {
  const analysis = analyzeHand(cards, wildJoker);
  const arranged = [];
  analysis.groups.forEach((g) => arranged.push(...g.cards));
  arranged.push(...analysis.deadwood);
  return { arrangedCards: arranged, analysis };
}

// A short human-readable label for a card, e.g. "7♥".
export const cardLabel = (card) => `${card.rank}${suitSymbol(card.suit)}`;

// Evaluate whether picking up a candidate card (e.g. the top of the discard pile)
// would improve the current hand. Useful for the "should I draw from the discard?"
// hint.
export function evaluatePickup(hand, candidate, wildJoker = null) {
  if (!candidate) {
    return { useful: false, description: 'The discard pile is empty.' };
  }
  const before = analyzeHand(hand, wildJoker);
  const after = analyzeHand([...(hand || []), candidate], wildJoker);
  const deadwoodReduction = before.deadwoodPoints - after.deadwoodPoints;
  const newGroups = after.groups.length - before.groups.length;
  const useful = deadwoodReduction > 0 || newGroups > 0;
  let description;
  if (useful) {
    const newGroup = after.groups[after.groups.length - 1];
    if (newGroups > 0 && newGroup) {
      description = `Pick up ${cardLabel(candidate)} — it forms a new ${newGroup.type === 'set' ? 'set' : 'sequence'}!`;
    } else {
      description = `Pick up ${cardLabel(candidate)} — it saves ${deadwoodReduction} deadwood point(s).`;
    }
  } else {
    description = `${cardLabel(candidate)} doesn't clearly help — drawing from the deck is the better choice.`;
  }
  return { useful, deadwoodReduction, newGroups, description };
}
